import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLang } from "../context/LanguageContext";
import { SuccessPanel, Consent } from "./FormKit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_RE = /^\+?[0-9\s\-().]{7,20}$/;

export default function InquiryForm({ path = "general", prefillMessage = "" }) {
  const { t, lang } = useLang();
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);
  const { register, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm({
    defaultValues: { message: prefillMessage || "" },
  });

  // When the turnkey CTA sets the prefill after mount, apply it to the message field.
  useEffect(() => {
    if (prefillMessage) setValue("message", prefillMessage);
  }, [prefillMessage, setValue]);

  const f = t("forms");
  const onSubmit = async (data) => {
    setError(false);
    if (data.website) return; // honeypot — silently drop
    try {
      const res = await fetch(`${API}/leads/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lang,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone: data.phone || "",
          path,
          message: data.message,
          website: data.website || "",
        }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch {
      setError(true);
    }
  };

  if (done) {
    return (
      <SuccessPanel
        testid="inquiry-form-success"
        title={t("forms.successTitle")}
        body={t("forms.successBody")}
        again={t("forms.again")}
        onAgain={() => { setDone(false); reset(); }}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="inquiry-form" noValidate>
      <div data-testid="field-first_name">
        <label className="field-label" htmlFor="first_name">{f.f_first} <span className="text-[#D1C7BD]">*</span></label>
        <input id="first_name" className="field-input" data-testid="input-first_name"
          {...register("first_name", { required: t("forms.required") })} />
        {errors.first_name && <p className="mono text-[10px] text-[#D1C7BD] mt-1.5 tracking-[0.14em] uppercase">{t("forms.required")}</p>}
      </div>
      <div data-testid="field-last_name">
        <label className="field-label" htmlFor="last_name">{f.f_last} <span className="text-[#D1C7BD]">*</span></label>
        <input id="last_name" className="field-input" data-testid="input-last_name"
          {...register("last_name", { required: t("forms.required") })} />
        {errors.last_name && <p className="mono text-[10px] text-[#D1C7BD] mt-1.5 tracking-[0.14em] uppercase">{t("forms.required")}</p>}
      </div>
      <div data-testid="field-email">
        <label className="field-label" htmlFor="email">{f.f_email} <span className="text-[#D1C7BD]">*</span></label>
        <input id="email" type="email" className="field-input" data-testid="input-email"
          {...register("email", {
            required: t("forms.required"),
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "invalidEmail" },
          })} />
        {errors.email && <p className="mono text-[10px] text-[#D1C7BD] mt-1.5 tracking-[0.14em] uppercase">{errors.email.message || t("forms.required")}</p>}
      </div>
      <div data-testid="field-phone">
        <label className="field-label" htmlFor="phone">{f.f_phone}</label>
        <input id="phone" type="tel" placeholder="+34 622 181 686" className="field-input" data-testid="input-phone"
          {...register("phone", { pattern: { value: PHONE_RE, message: "invalidPhone" } })} />
        {errors.phone && <p className="mono text-[10px] text-[#D1C7BD] mt-1.5 tracking-[0.14em] uppercase">{t("forms.phoneErr")}</p>}
      </div>
      <div className="md:col-span-2" data-testid="field-message">
        <label className="field-label" htmlFor="message">{f.f_message} <span className="text-[#D1C7BD]">*</span></label>
        <textarea id="message" rows={5} className="field-input" data-testid="input-message"
          placeholder={f.messagePlaceholder}
          {...register("message", { required: t("forms.required") })} />
        {errors.message && <p className="mono text-[10px] text-[#D1C7BD] mt-1.5 tracking-[0.14em] uppercase">{t("forms.required")}</p>}
      </div>
      {/* privacy consent */}
      <Consent name="privacy_consent" register={register} errors={errors} />
      {/* honeypot — invisible to humans */}
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden"
        style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
        {...register("website")} data-testid="input-website-honeypot" />
      <div className="md:col-span-2 flex flex-col gap-4">
        {error && <p className="mono text-[11px] text-[#D1C7BD] uppercase tracking-[0.14em]" data-testid="inquiry-form-error">{t("forms.error")}</p>}
        <button type="submit" disabled={isSubmitting} className="btn btn-solid justify-between md:self-start" data-testid="inquiry-form-submit-btn">
          <span>{isSubmitting ? t("forms.sending") : t("forms.submit")}</span>
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </form>
  );
}
